import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchPostDetails, createReplyPost, fetchPostReplies } from '../../../../api/playgroundServices';
import { UserType } from '../../../../types/userType';
import './PostDetails.css';
import { applyCollabs } from '../../../../api/collabsServices';
import { PostMedia, PostProps, ReplyPostData } from '../../../../types/postType';
import VIModal from '../../../common/VIModal/VIModal';
import PostReply from './PostReply';
import ApplyCollabsStepsModal from './ApplyCollabsStepsModal';
import MediaViewerModal from './MediaViewerModal';

// Import our new components
import PostDetailsHeader from './PostDetailsHeader';
import PostViewer from './PostViewer';
import RepliesSection from './RepliesSection';
import ReplyInputFooter from './ReplyInputFooter';
import { verifyAccessToken, isAuthenticated } from '../../../../api/auth';
import { trackCollabApplicationView } from '../../../../utils/tiktokTracking';

// Add a simple confirmation modal component
const EditApplicationModal = ({ isOpen, onClose, onConfirm }: { isOpen: boolean, onClose: () => void, onConfirm: () => void }) => {
  if (!isOpen) return null;
  
  return (
    <VIModal isOpen={isOpen} onClose={onClose}>
      <div className="edit-application-modal">
        <h3>You've already applied</h3>
        <p>Do you want to edit your application?</p>
        <div className="edit-application-buttons">
          <button className="secondary-button" onClick={onClose}>No</button>
          <button className="primary-button" onClick={onConfirm}>Yes, edit my application</button>
        </div>
      </div>
    </VIModal>
  );
};

// Add this new component at the top of the file, after the EditApplicationModal
const LoginRequiredModal = ({ isOpen, onClose }: { isOpen: boolean, onClose: () => void }) => {
  if (!isOpen) return null;
  
  return (
    <VIModal isOpen={isOpen} onClose={onClose}>
      <div className="login-required-modal">
        <h3>Login Required</h3>
        <p>You need to be signed in to apply for this collab.</p>
        <div className="login-required-buttons">
          <button className="primary-button" onClick={onClose}>Got it</button>
        </div>
      </div>
    </VIModal>
  );
};

// Interface for thread structure
interface ThreadedReply extends PostProps {
  childReplies?: ThreadedReply[];
}

// Function to organize replies into a threaded structure
const organizeRepliesIntoThreads = (replies: PostProps[]): ThreadedReply[] => {
  // First, create a map to store replies by their ID for quick lookup
  const replyMap = new Map<string | number, ThreadedReply>();
  
  // Initialize the top-level replies array
  const topLevelReplies: ThreadedReply[] = [];
  
  // Create threaded structure with childReplies arrays
  replies.forEach(reply => {
    // Create a copy of the reply with an empty childReplies array
    const threadedReply: ThreadedReply = {
      ...reply,
      childReplies: []
    };
    
    // Store in map for lookup
    replyMap.set(reply.id, threadedReply);
  });
  
  // Organize replies into parent-child relationships
  replies.forEach(reply => {
    const threadedReply = replyMap.get(reply.id)!;
    
    if (reply.parent_id && reply.parent_id !== null && replyMap.has(reply.parent_id)) {
      // This is a reply to another reply, add it to parent's childReplies
      const parentReply = replyMap.get(reply.parent_id);
      if (parentReply && parentReply.childReplies) {
        parentReply.childReplies.push(threadedReply);
      }
    } else if (reply.parent_id) {
      // This is a direct reply to the post, add to top level
      topLevelReplies.push(threadedReply);
    } else {
      // This is a top-level reply (if parent_id is null)
      topLevelReplies.push(threadedReply);
    }
  });
  
  return topLevelReplies;
};

const PostDetails: React.FC = () => {
  const { postSlug } = useParams<{ postSlug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<PostProps | null>(null);
  const [replies, setReplies] = useState<PostProps[]>([]);
  const [threadedReplies, setThreadedReplies] = useState<ThreadedReply[]>([]);
  const [currentUser, setCurrentUser] = useState<UserType | null>(null);
  const [canGoBack, setCanGoBack] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyMedia, setReplyMedia] = useState<File | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalReplies, setTotalReplies] = useState(0);
  const [replyingToPost, setReplyingToPost] = useState<PostProps | null>(null);
  const [replyingToReply, setReplyingToReply] = useState<ThreadedReply | null>(null);
  const observerTarget = useRef<HTMLDivElement>(null);
  const [usernameDict, setUsernameDict] = useState<{[key: string]: string}>({});
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showEditApplicationModal, setShowEditApplicationModal] = useState(false);
  const [isMediaViewerOpen, setIsMediaViewerOpen] = useState(false);
  const [mediaViewerIndex, setMediaViewerIndex] = useState(0);
  const [currentViewingMedia, setCurrentViewingMedia] = useState<PostMedia[] | null>(null);
  const [showLoginRequiredModal, setShowLoginRequiredModal] = useState(false);

  // Update this useEffect to properly check authentication
  useEffect(() => {
    const checkAuthAndLoadUser = async () => {
      if (isAuthenticated()) {
        try {
          const accessToken = localStorage.getItem('accessToken');
          if (accessToken) {
            const userData = await verifyAccessToken(accessToken);
            setCurrentUser(userData);
          }
        } catch (error) {
          console.error('Error verifying authentication:', error);
          // Token verification failed, user will remain logged out
        }
      } else {
        // Try to get user data from localStorage as fallback
        const userData = localStorage.getItem('userData');
        if (userData) {
          try {
            setCurrentUser(JSON.parse(userData));
          } catch (error) {
            console.error('Error parsing user data:', error);
          }
        }
      }
    };

    checkAuthAndLoadUser();
  }, []);

  // Process replies into threaded structure whenever flat replies change
  useEffect(() => {
    // Make sure replies is not empty before organizing
    if (replies && replies.length > 0) {
      // Filter to only include replies to the current post (not the post itself)
      const postReplies = replies.filter(reply => 
        reply.id !== post?.id && (reply.parent_id === post?.id || reply.parent_id !== null)
      );
      const threaded = organizeRepliesIntoThreads(postReplies);
      setThreadedReplies(threaded);
    } else {
      setThreadedReplies([]);
    }
  }, [replies, post]);

  useEffect(() => {
    const loadPostDetails = async () => {
      if (postSlug) {
        setIsLoading(true);
        try {
          const postData = await fetchPostDetails(postSlug);
          console.log('Post data with mentions:', postData); // Debug log
          setPost(postData);
          // Load initial replies
          const repliesData = await fetchPostReplies(postSlug);
          console.log('Replies data with mentions:', repliesData); // Debug log
          setReplies(repliesData.results);
          setTotalReplies(repliesData.count);
          setHasMore(!!repliesData.next);
        } catch (error) {
          console.error('Error loading post details:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadPostDetails();
  }, [postSlug]);

  // Handle loading more replies
  const handleLoadMoreReplies = async () => {
    if (!postSlug || isLoadingMore) return;
    
    setIsLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const repliesData = await fetchPostReplies(postSlug, nextPage);
      setReplies(prev => [...prev, ...repliesData.results]);
      setCurrentPage(nextPage);
      setHasMore(!!repliesData.next);
    } catch (error) {
      console.error('Error loading more replies:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    // Only show back button if user is logged in and has history
    if (currentUser && window.history.length > 1) {
      setCanGoBack(true);
    }
  }, [currentUser]);

  useEffect(() => {
    if (post) {
      console.log('Post loaded:', {
        postType: post.postType,
        isCollab: post.postType === 'collab',
        hasCollab: !!post.collab
      });
    }
  }, [post]);

  useEffect(() => {
    if (post && post.highlights) {
      // Extract user IDs from highlights that are mentions
      const userIds = post.highlights
        .filter(highlight => 
          highlight.highlight_type === 'mention' && 
          highlight.mention_type === 'user'
        )
        .map(highlight => highlight.user_id)
        .filter((id): id is number => id !== undefined);

      // Create initial mapping
      const initialDict: {[key: string]: string} = {};
      userIds.forEach(id => {
        if (post.highlight_username_dict?.[id]) {
          initialDict[id.toString()] = post.highlight_username_dict[id.toString()];
        }
      });
      setUsernameDict(initialDict);
    }
  }, [post]);

  const handleLoginModalClose = () => {
    setShowLoginRequiredModal(false);
  };

  const handleApplyButtonClick = async () => {
    // First check if user is logged in
    if (!currentUser) {
      // Check if we have tokens stored in localStorage before showing the sign-in modal
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        try {
          // Try to verify the token and set the user
          const userData = await verifyAccessToken(accessToken);
          setCurrentUser(userData);
          
          // Now that we have the user, proceed with the application flow
          if (post?.collabs_application_status) {
            // User has already applied, show confirmation modal
            setShowEditApplicationModal(true);
          } else {
            // First time applying, show apply modal directly
            setShowApplyModal(true);
          }
          return;
        } catch (error) {
          console.error('Error verifying access token:', error);
          // Token verification failed, continue to show sign-in modal
        }
      }
      
      // No valid tokens or verification failed, show sign-in modal
      setShowLoginRequiredModal(true);
      return;
    }
    
    // Track TikTok events for collab application view
    if (post) {
      await trackCollabApplicationView(
        String(post.id),
        post.content || 'Collaboration Application',
        currentUser
      );
    }
    
    // User is already logged in, proceed with normal flow
    if (post?.collabs_application_status) {
      // User has already applied, show confirmation modal
      setShowEditApplicationModal(true);
    } else {
      // First time applying, show apply modal directly
      setShowApplyModal(true);
    }
  };

  const handleEditConfirm = () => {
    // User confirmed they want to edit their application
    setShowEditApplicationModal(false);
    setShowApplyModal(true);
  };

  const handleReplyClick = (replyTarget?: ThreadedReply) => {
    if (!currentUser) {
      setShowSignInModal(true);
      return;
    }
    
    if (replyTarget) {
      // Set the reply we're responding to
      setReplyingToReply(replyTarget);
    } else {
      // Replying to the original post
      setReplyingToReply(null);
    }
    
    // Show the reply modal
    setShowReplyModal(true);
  };

  const handleReplyClose = () => {
    setShowReplyModal(false);
    setReplyingToReply(null);
  };

  const handleReplyContentChange = (content: string) => {
    setReplyText(content);
  };

  const handleReplyMediaSelect = (file: File | null) => {
    setReplyMedia(file);
  };

  const handleSubmitReply = async (text: string, images: File[], gif: string | null) => {
    try {
      if (!post || !currentUser) return;
      
      // If replying to a specific reply, use that reply's ID as parent_id
      const parentId = replyingToReply ? replyingToReply.id : post.id;
      
      // Create optimistic reply for immediate display
      const optimisticReply = {
        id: Date.now(),
        content: text,
        parentPostId: post.id,
        parent_id: parentId, // Use the appropriate parent_id
        user: {
          id: currentUser.id.toString(),
          avatar: currentUser.avatar,
          username: currentUser.username,
        },
        timestamp: new Date().toISOString(),
        postType: images.length > 0 ? 'media' : 'text',
        mediaList: images.map(file => ({
          url: URL.createObjectURL(file),
          file,
          media_type: 'image',
          width: 0,
          height: 0
        })),
        isSelfPost: true,
        currentUser: currentUser,
        currentUserId: currentUser.id.toString(),
        isNewPost: true,
        numLikes: 0,
        numReplies: 0,
        likedStatus: false,
        slug: `temp-${Date.now()}`,
        onPostDeleted: () => {},
        onReplyClick: () => {}
      } as PostProps;
      
      // Update UI immediately
      setReplies(prevReplies => [optimisticReply, ...prevReplies]);
      setTotalReplies(prev => prev + 1);
      
      // Close modal first
      setShowReplyModal(false);
      setReplyingToReply(null);
      
      // Then make API call (don't await)
      const replyData: ReplyPostData = {
        id: Date.now(),
        content: text,
        parentPostId: post.id,
        parent_id: parentId, // Use the parent ID appropriately
        user: {
          id: currentUser.id.toString(),
          avatar: currentUser.avatar,
          username: currentUser.username,
        },
        timestamp: new Date().toISOString(),
        postType: images.length > 0 ? 'media' : 'text',
        media: images.map(file => ({
          url: URL.createObjectURL(file),
          file,
          media_type: 'image',
          width: 0,
          height: 0
        })),
        isSelfPost: true,
        currentUser: currentUser,
        currentUserId: currentUser.id.toString(),
        isNewPost: true,
      };
      
      createReplyPost(replyData).then(() => {
        // Refresh data in background after API call completes
        fetchPostReplies(postSlug!).then(repliesData => {
          setReplies(repliesData.results);
          setTotalReplies(repliesData.count);
          setHasMore(!!repliesData.next);
        });
      }).catch(error => {
        console.error('Error creating reply:', error);
        // Refresh on error to ensure consistency
        fetchPostReplies(postSlug!).then(repliesData => {
          setReplies(repliesData.results);
          setTotalReplies(repliesData.count);
        });
      });
    } catch (error) {
      console.error('Error submitting reply:', error);
    }
  };

  const handleApplySubmit = async (text: string, images: File[], gif: string | null) => {
    if (!post || !currentUser || !post.collab) return;

    try {
      // Create temporary application/reply with dummy callback functions
      const tempApplication = {
        id: Date.now(),
        content: text,
        parentPostId: post.id,
        parent_id: post.id, // Set parent_id to the post id
        user: {
          id: currentUser.id.toString(),
          avatar: currentUser.avatar,
          username: currentUser.username,
        },
        timestamp: new Date().toISOString(),
        postType: images.length > 0 ? 'media' : 'text',
        mediaList: images.map(file => ({
          url: URL.createObjectURL(file),
          file,
          media_type: 'image',
          width: 0,
          height: 0
        })),
        isSelfPost: true,
        currentUser: currentUser,
        currentUserId: currentUser.id.toString(),
        isNewPost: true,
        numLikes: 0,
        numReplies: 0,
        likedStatus: false,
        slug: `temp-application-${Date.now()}`,
        // Add the required PostProps properties
        onPostDeleted: () => {}, // Empty function
        onReplyClick: () => {}   // Empty function
      } as PostProps;
      
      // Optimistically update UI
      setReplies(prevReplies => [tempApplication, ...prevReplies]);
      setTotalReplies(prev => prev + 1);
      
      // Submit to server
      await applyCollabs(post.collab.id, text, images);
      
      // Refresh data from server to get accurate state
      const repliesData = await fetchPostReplies(post.slug);
      setReplies(repliesData.results);
      setTotalReplies(repliesData.count);
      setCurrentPage(1);
      setHasMore(!!repliesData.next);
      
      // Reset UI state
      setShowApplyModal(false);
      setShowReplyModal(false);
    } catch (error) {
      console.error('Error applying to collab:', error);
      // On error, refresh from server to ensure UI is in sync
      const repliesData = await fetchPostReplies(post.slug);
      setReplies(repliesData.results);
      setTotalReplies(repliesData.count);
    }
  };

  const handleCancel = () => {
    setShowReplyModal(false);
    setIsApplying(false);
    setReplyingToReply(null);
  };

  const handlePostDeleted = (deletedPostId: number) => {
    // Remove post from replies if it is a reply
    setReplies(prevReplies => prevReplies.filter(reply => reply.id !== deletedPostId));
    setTotalReplies(prev => prev - 1);
  };

  const openMediaViewer = (mediaList: PostMedia[], startIndex: number) => {
    setCurrentViewingMedia(mediaList);
    setMediaViewerIndex(startIndex);
    setIsMediaViewerOpen(true);
  };

  const closeMediaViewer = () => {
    setIsMediaViewerOpen(false);
  };

  return (
    <div className="post-details">
      <PostDetailsHeader 
        canGoBack={canGoBack}
      />
      
      <div className="post-details-content">
        <PostViewer 
          post={post} 
          currentUser={currentUser} 
          isLoading={isLoading}
          onApplyClick={handleApplyButtonClick}
        />
        
        <RepliesSection
          replies={replies}
          totalReplies={totalReplies}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          currentUser={currentUser}
          postType={post?.postType}
          onPostDeleted={handlePostDeleted}
          onIntersection={handleLoadMoreReplies}
        />
      </div>
      
      {post && (
        <ReplyInputFooter
          currentUser={currentUser}
          originalPostUsername={post.user.username}
          onReplyClick={() => handleReplyClick()}
        />
      )}

      {post && (
        <ApplyCollabsStepsModal
          isOpen={showApplyModal}
          onClose={() => setShowApplyModal(false)}
          onSubmit={handleApplySubmit}
          originalPost={post}
          currentUser={currentUser}
        />
      )}
      
      <EditApplicationModal
        isOpen={showEditApplicationModal}
        onClose={() => setShowEditApplicationModal(false)}
        onConfirm={handleEditConfirm}
      />
      
      <MediaViewerModal
        isOpen={isMediaViewerOpen}
        onClose={closeMediaViewer}
        mediaList={currentViewingMedia || []}
        initialIndex={mediaViewerIndex}
      />

      <LoginRequiredModal 
        isOpen={showLoginRequiredModal}
        onClose={handleLoginModalClose}
      />
      
      {showSignInModal && (
        <VIModal isOpen={showSignInModal} onClose={() => setShowSignInModal(false)}>
          <div className="signin-required-modal">
            <h3>Sign in required</h3>
            <p>You need to sign in to apply for this collab.</p>
            <div className="signin-required-buttons">
              <button className="secondary-button" onClick={() => setShowSignInModal(false)}>Cancel</button>
              <button 
                className="primary-button" 
                onClick={() => {
                  setShowSignInModal(false);
                  navigate('/signin');
                }}
              >
                Sign in
              </button>
            </div>
          </div>
        </VIModal>
      )}
    </div>
  );
};

export default PostDetails;
